import { Link, useNavigate } from "react-router-dom";
import { XStack, YStack } from "tamagui";

import BetaJoinCard from "@/components/BetaJoinCard";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppContentContainer,
  AppHeading,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";
import {
  PublicBrandCard,
  PublicMarketingFooter,
  PublicMarketingPage,
} from "@/components/PublicMarketingScaffold";
import { getKindredPublicTagline, getKindredSupportFeatureDescription } from "../../shared/support";

const HOW_IT_WORKS = [
  {
    step: "1. Set up your profile",
    title: "Make Kindred feel local and relevant",
    body:
      "Share your home location and family stage so the app can surface nearby families, useful conversations, and child-friendly plans that fit real life.",
  },
  {
    step: "2. Find your people",
    title: "Meet single parent families who actually get it",
    body:
      "Use Kinfeed and communities to start with warm conversation instead of dropping into generic groups or awkward dating-led spaces.",
  },
  {
    step: "3. Join in and get support",
    title: "Make plans and get practical help in one place",
    body:
      "Move from scrolling to real activity by finding family plans, local events, and practical local support without jumping between separate apps.",
  },
];

const FEATURES = [
  {
    title: "Kinfeed gives you a warm front door into nearby conversation and activity",
    body:
      "See what people are talking about, where families are heading, and where to start without wondering which part of the app matters first.",
  },
  {
    title: "Community helps you find the right people faster",
    body:
      "Topic and location-based spaces make connection feel less awkward and more relevant than generic parent groups or giant social feeds.",
  },
  {
    title: "Activities make weekends and school holidays easier to plan",
    body:
      "Find group plans and What’s On ideas in one place so you are not building family time from scratch every single week.",
  },
  {
    title: "Support keeps practical help close at hand",
    body:
      "Local guidance sits alongside the community, so when life gets heavy you can find useful support without leaving the app and starting a new search.",
  },
];

const PREVIEW_PANELS = [
  {
    label: "Kinfeed",
    title: "A warm front door into nearby conversation",
    body: "See what other single parent families are talking about and where you can join in.",
  },
  {
    label: "Activities",
    title: "Child-friendly plans that feel easier to say yes to",
    body: "Spot group plans and What’s On ideas without piecing the weekend together from three different apps.",
  },
  {
    label: "Support",
    title: "Practical help when life feels heavy",
    body: getKindredSupportFeatureDescription(),
  },
];

const WHY_IT_HELPS = [
  "Meet other single parent families without wondering if an app is secretly for dating.",
  "Make weekends and school holidays easier to plan without starting from zero each time.",
  "Keep practical local support close to the same place you go for community and activities.",
];

const LandingAlternative = () => {
  const navigate = useNavigate();

  return (
    <PublicMarketingPage>
      <section className="kindred-brand-gradient py-12 text-center">
        <AppContentContainer>
          <YStack gap="$lg">
            <XStack alignItems="center" justifyContent="space-between" gap="$sm" flexWrap="wrap">
              <PublicBrandCard />
              <Link to="/signin" style={{ textDecoration: "none" }}>
                <AppText
                  fontFamily={kindredFontFamilies.sansBold}
                  fontSize="$sm"
                  lineHeight="$sm"
                  style={{ color: "rgba(255,255,255,0.92)" }}
                >
                  Sign in
                </AppText>
              </Link>
            </XStack>

            <YStack alignItems="center" alignSelf="center" gap="$sm" maxWidth={800}>
              <AppText
                textAlign="center"
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$sm"
                lineHeight="$sm"
                textTransform="uppercase"
                letterSpacing={2.4}
                color="$accent"
              >
                {getKindredPublicTagline()}
              </AppText>
              <AppHeading
                textAlign="center"
                color="$primaryForeground"
                fontSize="$hero"
                lineHeight="$hero"
                letterSpacing="$hero"
                $gtSm={{ fontSize: "$heroLg", lineHeight: "$heroLg", letterSpacing: "$heroLg" }}
              >
                Find your people, child-friendly plans, and practical support in one free community for single parent
                families
              </AppHeading>
              <AppText
                textAlign="center"
                fontSize="$lg"
                lineHeight="$xl"
                maxWidth={760}
                style={{ color: "rgba(255,255,255,0.92)" }}
              >
                Kindred helps single mums, single dads, and solo parent families meet people locally, discover
                family-friendly things to do, and get practical support without juggling Facebook groups, WhatsApp
                chats, generic event apps, and dating-led platforms. Kindred is opening carefully across the UK,
                Australia, New Zealand, and the United States. Local support guidance is now live across those
                active rollout countries, with the U.S. set using one shared national library plus routed state,
                district, tribal, and local next steps where the system changes by place. Republic of Ireland is
                paused while Kindred resolves the EEA representative position, and Canada is paused for later while
                Kindred prepares a proper Quebec/French public, legal, and support layer.
              </AppText>
              <div className="mt-2 flex w-full flex-col items-center justify-center gap-3 sm:flex-row">
                <AppButton
                  onPress={() => navigate("/signup")}
                  backgroundColor="$cardBackground"
                  borderColor="$cardBackground"
                  color="$primary"
                  minWidth={180}
                >
                  Join Kindred free
                </AppButton>
                <AppButton
                  tone="outline"
                  onPress={() => navigate("/signin")}
                  backgroundColor="transparent"
                  borderColor="$cardBackground"
                  color="$cardBackground"
                  minWidth={180}
                >
                  Sign in
                </AppButton>
              </div>
              <AppText textAlign="center" fontSize="$sm" lineHeight="$sm" style={{ color: "rgba(255,255,255,0.86)" }}>
                Free to access. No paywalls. No dating section.
              </AppText>
            </YStack>

            <div className="grid gap-4 lg:grid-cols-[1.15fr,0.85fr]">
              <AppCard className="kindred-soft-panel text-left">
                <AppCardBody gap="$md">
                  <AppText className="kindred-kicker">Kindred preview</AppText>
                  <AppHeading fontSize="$xxl" lineHeight="$xxl" letterSpacing="$xxl">
                    What you can do inside
                  </AppHeading>
                  <div className="grid gap-4 md:grid-cols-3">
                    {PREVIEW_PANELS.map((panel) => (
                      <AppCard key={panel.label} backgroundColor="$background">
                        <AppCardBody py="$md">
                          <AppText
                            fontFamily={kindredFontFamilies.sansBold}
                            fontSize="$xs"
                            lineHeight="$xs"
                            textTransform="uppercase"
                            letterSpacing={1.8}
                            color="$primary"
                            opacity={0.7}
                          >
                            {panel.label}
                          </AppText>
                          <AppText
                            fontFamily={kindredFontFamilies.sansBold}
                            fontSize="$md"
                            lineHeight="$md"
                            color="$primary"
                          >
                            {panel.title}
                          </AppText>
                          <AppText fontSize="$sm" lineHeight="$md" color="$colorMuted">
                            {panel.body}
                          </AppText>
                        </AppCardBody>
                      </AppCard>
                    ))}
                  </div>
                </AppCardBody>
              </AppCard>

              <AppCard className="kindred-soft-panel text-left">
                <AppCardBody gap="$md">
                  <AppText className="kindred-kicker">Why it helps</AppText>
                  <YStack gap="$sm">
                    {WHY_IT_HELPS.map((item) => (
                      <XStack key={item} alignItems="flex-start" gap="$sm">
                        <AppText color="$primary">✓</AppText>
                        <AppText flex={1} fontSize="$sm" lineHeight="$md" color="$colorMuted">
                          {item}
                        </AppText>
                      </XStack>
                    ))}
                  </YStack>
                </AppCardBody>
              </AppCard>
            </div>
          </YStack>
        </AppContentContainer>
      </section>

      <section className="bg-card py-14">
        <AppContentContainer>
          <YStack gap="$lg">
            <YStack alignItems="center" alignSelf="center" gap="$sm" maxWidth={640}>
              <AppHeading textAlign="center" fontSize="$xxl" lineHeight="$xxl" letterSpacing="$xxl">
                How it works
              </AppHeading>
              <AppText textAlign="center" fontSize="$md" lineHeight="$lg" color="$colorMuted">
                Kindred is meant to get you to useful people and plans quickly, not bury you in setup.
              </AppText>
            </YStack>
            <div className="grid gap-4 md:grid-cols-3">
              {HOW_IT_WORKS.map((item) => (
                <AppCard key={item.step} backgroundColor="$background">
                  <AppCardBody py="$md">
                    <AppText
                      fontFamily={kindredFontFamilies.sansBold}
                      fontSize="$xs"
                      lineHeight="$xs"
                      textTransform="uppercase"
                      letterSpacing={1.8}
                      color="$primary"
                      opacity={0.7}
                    >
                      {item.step}
                    </AppText>
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" lineHeight="$lg" color="$primary">
                      {item.title}
                    </AppText>
                    <AppText fontSize="$sm" lineHeight="$md" color="$colorMuted">
                      {item.body}
                    </AppText>
                  </AppCardBody>
                </AppCard>
              ))}
            </div>
          </YStack>
        </AppContentContainer>
      </section>

      <section className="kindred-plum-surface py-14">
        <AppContentContainer>
          <YStack gap="$lg">
            <YStack alignItems="center" alignSelf="center" gap="$sm" maxWidth={680}>
              <AppHeading textAlign="center" fontSize="$xxl" lineHeight="$xxl" letterSpacing="$xxl">
                What makes Kindred different
              </AppHeading>
              <AppText textAlign="center" fontSize="$md" lineHeight="$lg" color="$colorMuted">
                It is not just another social app. It is built around single parent family life as it is actually lived.
              </AppText>
            </YStack>
            <div className="grid gap-4 md:grid-cols-2">
              {FEATURES.map((feature) => (
                <AppCard key={feature.title} backgroundColor="$background">
                  <AppCardBody py="$md">
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" lineHeight="$lg" color="$primary">
                      {feature.title}
                    </AppText>
                    <AppText fontSize="$sm" lineHeight="$md" color="$colorMuted">
                      {feature.body}
                    </AppText>
                  </AppCardBody>
                </AppCard>
              ))}
            </div>
          </YStack>
        </AppContentContainer>
      </section>

      <section className="bg-card py-14">
        <AppContentContainer>
          <AppCard backgroundColor="$background">
            <AppCardBody alignItems="center" py="$xl">
              <AppText
                textAlign="center"
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$sm"
                lineHeight="$sm"
                textTransform="uppercase"
                letterSpacing={2.4}
                color="$primary"
                opacity={0.7}
              >
                Free to join
              </AppText>
              <AppHeading textAlign="center" fontSize="$xxl" lineHeight="$xxl" letterSpacing="$xxl" maxWidth={680}>
                No paywalls. No dating section. No card required.
              </AppHeading>
              <AppText textAlign="center" fontSize="$md" lineHeight="$lg" color="$colorMuted" maxWidth={720}>
                Kindred is built to help single parent families find people nearby, make plans, and get support in one
                place without hitting locked features or confusing mixed-purpose social spaces.
              </AppText>
              <div className="mt-2 flex flex-col justify-center gap-3 sm:flex-row">
                <AppButton onPress={() => navigate("/signup")} minWidth={180}>
                  Join Kindred free
                </AppButton>
                <AppButton tone="outline" onPress={() => navigate("/signin")} minWidth={180}>
                  Sign in
                </AppButton>
              </div>
            </AppCardBody>
          </AppCard>
        </AppContentContainer>
      </section>

      <section className="bg-card pb-14">
        <AppContentContainer>
          <BetaJoinCard id="android-beta-testing" />
        </AppContentContainer>
      </section>

      <section className="kindred-brand-gradient py-14 text-center">
        <AppContentContainer>
          <YStack alignItems="center" gap="$sm">
            <AppHeading textAlign="center" color="$primaryForeground" fontSize="$xxl" lineHeight="$xxl" letterSpacing="$xxl">
              Ready to feel less alone in this?
            </AppHeading>
            <AppText
              textAlign="center"
              fontSize="$md"
              lineHeight="$lg"
              maxWidth={560}
              style={{ color: "rgba(255,255,255,0.8)" }}
            >
              Meet people nearby, make family plans, and keep practical support close at hand in one calm community.
            </AppText>
            <AppButton
              marginTop="$sm"
              onPress={() => navigate("/signup")}
              backgroundColor="$cardBackground"
              borderColor="$cardBackground"
              color="$primary"
            >
              Join Kindred
            </AppButton>
          </YStack>
        </AppContentContainer>
      </section>

      <PublicMarketingFooter />
    </PublicMarketingPage>
  );
};

export default LandingAlternative;
